import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as SparePartListActions from './sparepart-list.actions';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { SparePartListFacade } from './sparepart-list.facade';
import { TSparePartListInfoActionType } from './sparepart-list.models';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class SparePartListEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private sparePartListFacade: SparePartListFacade,
    private APIHelper: AppNotificationUtilClass,
    private router: Router,
  ) {}

  getSparePartList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TSparePartListInfoActionType.GetSparePartList),
      switchMap((action: SparePartListActions.GetSparePartListInfo) =>
        this.sparePartListFacade.getSparepartListPagination().pipe(
          take(1),
          map((pagination) => {
            return [action, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, pagination]) =>
        this.mmDataService
          .fetchSparePartListData({
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
            selectedFilters: action.selectedFilters,
            searchStrOpt: action.searchStrOpt,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new SparePartListActions.GetSparePartListInfoSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new SparePartListActions.GetSparePartListInfoFailure());
            }),
          ),
      ),
    ),
  );

  updateSparePart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TSparePartListInfoActionType.UpdateSparePart),
      switchMap((action: SparePartListActions.UpdateSparePart) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.updateSparePart(action.updatedSparePart).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.sparePartListFacade.resetSparePartListInfo('ACTION');
            return new SparePartListActions.UpdateSparePartSuccess();
          }),
          catchError(() => {
            return of(new SparePartListActions.UpdateSparePartFailure());
          }),
        ),
      ),
    ),
  );

  bulkUpdateSparePartThreshold$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TSparePartListInfoActionType.BulkUpdateSparePartThreshold),
      switchMap((action: SparePartListActions.BulkUpdateSparePartThreshold) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.bulkUpdateSparePartThreshold(action.updatedSparePart).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.sparePartListFacade.resetSparePartListInfo('ACTION');
            return new SparePartListActions.BulkUpdateSparePartThresholdSuccess();
          }),
          catchError(() => {
            return of(new SparePartListActions.BulkUpdateSparePartThresholdFailure());
          }),
        ),
      ),
    ),
  );

  deleteSpareParts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TSparePartListInfoActionType.DeleteSpareParts),
      switchMap((action: SparePartListActions.DeleteSpareParts) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService
          .deleteSpareParts({
            sparePartIds: action.sparePartIds,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map(() => {
              this.sparePartListFacade.resetSparePartListInfo('ACTION');
              return new SparePartListActions.DeleteSparePartsSuccess();
            }),
            catchError(() => {
              return of(new SparePartListActions.DeleteSparePartsFailure());
            }),
          ),
      ),
    ),
  );

  fetchSparePartRequestListInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TSparePartListInfoActionType.GetSparePartRequestList),
      switchMap((action: SparePartListActions.GetSparePartRequestListInfo) =>
        this.sparePartListFacade.getSparepartRequestPagination().pipe(
          take(1),
          map((pagination) => {
            return { action, pagination };
          }),
        ),
      ),
      switchMap(({ action, pagination }) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return { action, pagination, loaderEl };
          }),
        ),
      ),
      switchMap((args) =>
        this.mmDataService
          .fetchSparePartRequestListData({
            ...args.action.payload,
            page: args.pagination.pageNo,
            itemsPerPage: args.pagination.pageSize,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(args.loaderEl);
            }),
            map((res) => new SparePartListActions.GetSparePartRequestListInfoSuccess(res, args.action.fetchReason)),
            catchError(() => {
              return of(new SparePartListActions.GetSparePartRequestListInfoFailure());
            }),
          ),
      ),
    ),
  );

  updateSparePartRequestStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TSparePartListInfoActionType.BulkUpdateSparePartRequestStatus),
      switchMap((action: SparePartListActions.BulkUpdateSparePartRequestStatus) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.updateSparePartRequestStatus(action.updatedSparePartRequest).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            const updateStatus = action.updatedSparePartRequest.updatedStatus as string;
            this.router.navigate(['/approvals/sparepart-request'], {
              queryParams: { requestStatus: updateStatus },
            });
            return new SparePartListActions.BulkUpdateSparePartRequestStatusSuccess();
          }),
          catchError(() => {
            return of(new SparePartListActions.BulkUpdateSparePartRequestStatusFailure());
          }),
        ),
      ),
    ),
  );

  bulkUpdateSparePartCritical$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TSparePartListInfoActionType.BulkUpdateSparePartCritical),
      switchMap((action: SparePartListActions.BulkUpdateSparePartCritical) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.bulkUpdateSparePartCritical(action.updatedSparePart).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.sparePartListFacade.resetSparePartListInfo('ACTION');
            return new SparePartListActions.BulkUpdateSparePartCriticalSuccess();
          }),
          catchError(() => {
            return of(new SparePartListActions.BulkUpdateSparePartCriticalFailure());
          }),
        ),
      ),
    ),
  );
}
